import React, { useState, useEffect } from 'react';
import { useLocation, Link, useNavigate } from 'react-router-dom';
import {
  Row,
  Col,
  Alert,
  Container,
  Form,
  Input,
  FormFeedback,
  Label,
  Spinner
} from 'reactstrap';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import { verifyUser } from '../../axios/forgotPassword';
import logo from '../../assets/images/logov1.1.png';

const VerifyInvite = () => {
  document.title = 'Accept Invitation | Seamless Slot';

  const location = useLocation();
  const navigate = useNavigate();

  const [token, setToken] = useState(null);
  const [userId, setUserId] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [success, setSuccess] = useState(false);

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tokenFromURL = params.get('token');
    const userIdFromURL = params.get('userId');

    if (tokenFromURL && userIdFromURL) {
      setToken(tokenFromURL);
      setUserId(userIdFromURL);
    } else {
      setErrorMessage('Invalid invitation link');
    }
  }, [location]);

  const validation = useFormik({
    initialValues: {
      password: '',
      confirmPassword: ''
    },
    validationSchema: Yup.object({
      password: Yup.string()
        .min(6, 'Password must be at least 6 characters')
        .required('Please enter password'),
      confirmPassword: Yup.string()
        .oneOf([Yup.ref('password')], 'Passwords must match')
        .required('Please confirm password')
    }),
    onSubmit: async (values, { setSubmitting }) => {
      try {
        const payload = {
          password: values.password,
          token,
          userId: parseInt(userId)
        };

        const response = await verifyUser(payload);
        if (response?.status === 200 || response?.status === 201) {
          setSuccess(true);
        }
      } catch (err) {
        setErrorMessage('This invitation link has expired.');
      } finally {
        setSubmitting(false);
      }
    }
  });

  return (
    <div style={{ background: '#fff', minHeight: '100vh' }}>
      <Container>
        <Row className="justify-content-center">
          <Col md={5} lg={4}>
            {/* Header */}
            <div className="text-center mb-5" style={{ paddingTop: 80 }}>
              <Link to="/">
                <img src={logo} alt="Logo" height="24" className="mb-5" />
              </Link>
              <h2 className="fw-bold" style={{ fontSize: 42, color: '#000' }}>
                Accept Invitation
              </h2>
              <p className="text-muted mt-2">
                Create a password to activate your account
              </p>
            </div>

            {errorMessage && <Alert color="danger">{errorMessage}</Alert>}

            {success ? (
              <Alert color="success" className="text-center">
                <h5 className="mb-2">Account Activated 🎉</h5>
                <p className="mb-3">Your password has been set successfully.</p>
                <button
                  className="btn btn-dark w-100"
                  onClick={() => navigate('/login')}
                >
                  Go to Login
                </button>
              </Alert>
            ) : (
              <Form
                onSubmit={(e) => {
                  e.preventDefault();
                  validation.handleSubmit();
                }}
              >
                {/* Password */}
                <div className="mb-3">
                  <Label>Password</Label>
                  <div className="input-group">
                    <Input
                      name="password"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="Password"
                      value={validation.values.password}
                      onChange={validation.handleChange}
                      onBlur={validation.handleBlur}
                      style={{
                        paddingTop: 13,
                        paddingBottom: 13,
                        paddingLeft: 12,
                        borderRadius: 3,
                        fontSize: 16
                      }}
                      invalid={
                        validation.touched.password &&
                        !!validation.errors.password
                      }
                    />
                    <span
                      className="input-group-text"
                      style={{ cursor: 'pointer' }}
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      <i
                        className={`mdi ${
                          showPassword ? 'mdi-eye-off' : 'mdi-eye'
                        }`}
                      />
                    </span>
                  </div>
                  <FormFeedback>{validation.errors.password}</FormFeedback>
                </div>

                {/* Confirm Password */}
                <div className="mb-4">
                  <Label>Confirm Password</Label>
                  <div className="input-group">
                    <Input
                      name="confirmPassword"
                      type={showConfirmPassword ? 'text' : 'password'}
                      placeholder="Confirm Password"
                      value={validation.values.confirmPassword}
                      onChange={validation.handleChange}
                      onBlur={validation.handleBlur}
                      style={{
                        paddingTop: 13,
                        paddingBottom: 13,
                        paddingLeft: 12,
                        borderRadius: 3,
                        fontSize: 16
                      }}
                      invalid={
                        validation.touched.confirmPassword &&
                        !!validation.errors.confirmPassword
                      }
                    />
                    <span
                      className="input-group-text"
                      style={{ cursor: 'pointer' }}
                      onClick={() =>
                        setShowConfirmPassword(!showConfirmPassword)
                      }
                    >
                      <i
                        className={`mdi ${
                          showConfirmPassword ? 'mdi-eye-off' : 'mdi-eye'
                        }`}
                      />
                    </span>
                  </div>
                  <FormFeedback>
                    {validation.errors.confirmPassword}
                  </FormFeedback>
                </div>

                {/* Submit */}
                <button
                  type="submit"
                  className="btn btn-dark w-100"
                  style={{
                    paddingTop: 15,
                    paddingBottom: 15,
                    borderRadius: 3,
                    fontSize: 16
                  }}
                  disabled={
                    validation.isSubmitting ||
                    !validation.isValid ||
                    !validation.dirty
                  }
                >
                  {validation.isSubmitting ? (
                    <Spinner size="sm" />
                  ) : (
                    'Accept Invitation'
                  )}
                </button>
              </Form>
            )}

            {/* Footer */}
            <div className="text-center mt-5 text-muted small">
              <span
                className="mx-2"
                style={{ cursor: 'pointer' }}
                onClick={() => navigate('/privacypolicy')}
              >
                Privacy policy
              </span>
              |
              <span
                className="mx-2"
                style={{ cursor: 'pointer' }}
                onClick={() => navigate('/terms')}
              >
                Terms
              </span>
              |
              <span
                className="mx-2"
                style={{ cursor: 'pointer' }}
                onClick={() => navigate('/contactus')}
              >
                Help
              </span>
            </div>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default VerifyInvite;
